import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Autocomplete,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import api from '../../utils/api';
import useCountries from '../../hooks/useCountries';
import {
  resolveCountryIso,
  isUSCountry,
  isIndiaCountry,
} from '../../utils/addressCountry';

function zipDigits(z) {
  return String(z || '').replace(/\D/g, '');
}

function useDebounced(value, ms) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return d;
}

function findStateCode(iso, stateVal, statesList) {
  if (!stateVal || !statesList?.length) return '';
  const s = String(stateVal).trim();
  const byCode = statesList.find((x) => x.isoCode === s);
  if (byCode) return byCode.isoCode;
  const byName = statesList.find((x) => x.name.toLowerCase() === s.toLowerCase());
  return byName?.isoCode || s;
}

/**
 * Searchable country, US/IN state dropdowns, optional US/IN address autocomplete (Photon via API),
 * postal lookup (US ZIP / India PIN).
 */
const StructuredAddressFields = ({
  title = 'Address',
  values,
  onPatch,
  disabled,
  zipHelperText,
}) => {
  const { country, state, city, zip_code, address_line1, address_line2 } = values;
  const [zipLookupMsg, setZipLookupMsg] = useState('');
  const { countries: seededCountries, loading: countriesLoading } = useCountries();
  const [statesModule, setStatesModule] = useState(null);
  const [addrInput, setAddrInput] = useState(address_line1 || '');
  const [addrOptions, setAddrOptions] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);

  useEffect(() => {
    let cancelled;
    import('country-state-city').then((mod) => {
      if (!cancelled) setStatesModule(mod);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const countries = useMemo(
    () =>
      (seededCountries || []).map((c) => ({
        isoCode: c.code || c.isoCode,
        name: c.label || c.name,
      })),
    [seededCountries]
  );
  const countryIso = useMemo(
    () => resolveCountryIso(country, countries),
    [country, countries]
  );

  const stateRows = useMemo(() => {
    if (!statesModule || !countryIso) return [];
    if (countryIso === 'US' || countryIso === 'IN') {
      return statesModule.State.getStatesOfCountry(countryIso);
    }
    return [];
  }, [statesModule, countryIso]);

  const stateSelectValue = useMemo(
    () => findStateCode(countryIso, state, stateRows),
    [countryIso, state, stateRows]
  );

  useEffect(() => {
    setAddrInput(address_line1 || '');
  }, [address_line1]);

  const debouncedAddrQ = useDebounced(addrInput, 400);

  const fetchAddressSuggestions = useCallback(async () => {
    const iso = countryIso;
    if (!iso || (iso !== 'US' && iso !== 'IN')) {
      setAddrOptions([]);
      return;
    }
    const q = debouncedAddrQ.trim();
    if (q.length < 3) {
      setAddrOptions([]);
      return;
    }
    setAddrLoading(true);
    try {
      const cc = iso.toLowerCase();
      const res = await api.get('/utils/address-suggest', { params: { q, cc } });
      const list = res.data?.data?.suggestions || [];
      setAddrOptions(Array.isArray(list) ? list : []);
    } catch {
      setAddrOptions([]);
    } finally {
      setAddrLoading(false);
    }
  }, [debouncedAddrQ, countryIso]);

  useEffect(() => {
    fetchAddressSuggestions();
  }, [fetchAddressSuggestions]);

  const runPostalLookup = useCallback(async () => {
    setZipLookupMsg('');
    const d = zipDigits(zip_code);

    if (isUSCountry(country) || countryIso === 'US') {
      const zip5 = d.slice(0, 5);
      if (zip5.length !== 5) return;
      const pathZip = d.length >= 9 ? `${zip5}-${d.slice(5, 9)}` : zip5;
      try {
        const res = await api.get(`/utils/us-zip/${pathZip}`);
        const payload = res.data?.data;
        if (payload?.city != null || payload?.state != null) {
          onPatch({
            city: payload.city || city,
            state: payload.state || state,
            zip_code: payload.zip_code || zip_code,
          });
        }
      } catch (e) {
        if (e.response?.status === 404) setZipLookupMsg('ZIP not found');
      }
      return;
    }

    if (isIndiaCountry(country) || countryIso === 'IN') {
      const pin = d.slice(0, 6);
      if (pin.length !== 6) return;
      try {
        const res = await api.get(`/utils/in-pin/${pin}`);
        const payload = res.data?.data;
        if (payload?.city != null || payload?.state != null) {
          onPatch({
            city: payload.city || city,
            state: payload.state || state,
            zip_code: payload.zip_code || zip_code,
          });
        }
      } catch (e) {
        if (e.response?.status === 404) setZipLookupMsg('PIN not found');
      }
    }
  }, [country, countryIso, zip_code, city, state, onPatch]);

  const postalHelper =
    zipLookupMsg ||
    zipHelperText ||
    (isUSCountry(country) || countryIso === 'US'
      ? 'US: type ZIP and tab out to fill city & state'
      : isIndiaCountry(country) || countryIso === 'IN'
        ? 'India: type 6-digit PIN and tab out to fill city & state'
        : '');

  const showAddrAutocomplete = countryIso === 'US' || countryIso === 'IN';

  return (
    <>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={countries}
            loading={countriesLoading || !countries.length}
            disabled={disabled || (!countries.length && countriesLoading)}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
            isOptionEqualToValue={(a, b) => a.isoCode === b.isoCode}
            value={countries.find((c) => c.isoCode === countryIso) || null}
            onChange={(e, opt) => {
              onPatch({
                country: opt?.isoCode || '',
                ...(opt?.isoCode && opt.isoCode !== 'US' && opt.isoCode !== 'IN' ? { state: '' } : {}),
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                placeholder="Search country"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {!countries.length ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Choose country first. US &amp; India: state dropdown, PIN/ZIP lookup, and address suggestions
            (OpenStreetMap via Photon).
          </Typography>
        </Grid>

        <Grid item xs={12}>
          {showAddrAutocomplete ? (
            <Autocomplete
              freeSolo
              filterOptions={(x) => x}
              options={addrOptions}
              loading={addrLoading}
              disabled={disabled}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
              inputValue={addrInput}
              onInputChange={(e, v, reason) => {
                if (reason === 'input') {
                  setAddrInput(v);
                  onPatch({ address_line1: v });
                }
              }}
              onChange={(e, opt) => {
                if (opt && typeof opt === 'object' && opt.label) {
                  onPatch({
                    address_line1: opt.address_line1 || opt.label,
                    city: opt.city || city,
                    state: opt.state || state,
                    zip_code: opt.zip_code || zip_code,
                    ...(opt.country && opt.country.length === 2 ? { country: opt.country } : {}),
                  });
                  setAddrInput(opt.address_line1 || opt.label);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Address line 1"
                  name="address_line1"
                  helperText="Type street number &amp; name — pick a suggestion or keep typing"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {addrLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : (
            <TextField
              fullWidth
              label="Address line 1"
              name="address_line1"
              value={address_line1 || ''}
              onChange={(e) => onPatch({ address_line1: e.target.value })}
              disabled={disabled}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address line 2"
            name="address_line2"
            value={address_line2 || ''}
            onChange={(e) => onPatch({ address_line2: e.target.value })}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="City"
            name="city"
            value={city || ''}
            onChange={(e) => onPatch({ city: e.target.value })}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          {stateRows.length > 0 ? (
            <TextField
              key={countryIso}
              select
              fullWidth
              label="State / Province"
              name="state"
              value={stateSelectValue || ''}
              onChange={(e) => onPatch({ state: e.target.value })}
              disabled={disabled}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {stateRows.map((row) => (
                <MenuItem key={row.isoCode} value={row.isoCode}>
                  {row.name}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              fullWidth
              label="State / Province"
              name="state"
              value={state || ''}
              onChange={(e) => onPatch({ state: e.target.value })}
              disabled={disabled}
            />
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={isIndiaCountry(country) || countryIso === 'IN' ? 'PIN code' : 'ZIP / Postal code'}
            name="zip_code"
            value={zip_code || ''}
            onChange={(e) => onPatch({ zip_code: e.target.value })}
            onBlur={runPostalLookup}
            disabled={disabled}
            helperText={postalHelper}
            error={Boolean(zipLookupMsg)}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default StructuredAddressFields;
